import { Skill, Education, Certification, ContactInfo, ResearchInterest } from "../types";

export const skills: Skill[] = [
  {
    category: "Programming Languages",
    skills: [
      "Python",
      "C/C++",
      "Golang",
      "Rust",
      "JavaScript/TypeScript",
      "SQL",
      "Shell Scripting",
    ],
    proficiency: "advanced",
  },
  {
    category: "Machine Learning & AI",
    skills: [
      "TensorFlow",
      "PyTorch",
      "Deep Learning",
      "Neural Networks",
      "Reinforcement Learning",
      "Signal Processing",
      "Time Series Analysis",
      "Computer Vision",
    ],
    proficiency: "advanced",
  },
  {
    category: "Systems & Networks",
    skills: [
      "Linux/UNIX",
      "Distributed Systems",
      "Network Protocols",
      "TCP/IP",
      "5G/6G Networks",
      "MQTT",
      "ZeroMQ",
      "Real-time Systems",
    ],
    proficiency: "advanced",
  },
  {
    category: "Research Tools",
    skills: [
      "OMNET++",
      "Network Simulation",
      "Scientific Computing",
      "Data Analysis",
      "LaTeX",
      "Jupyter",
      "Git",
      "Docker",
    ],
    proficiency: "advanced",
  },
  {
    category: "Development & Cloud",
    skills: [
      "AWS (Lambda, ECS, Cognito)",
      "CI/CD",
      "Docker/Kubernetes",
      "Terraform",
      "Linux Administration",
      "System Design",
    ],
    proficiency: "intermediate",
  },
];

export const education: Education[] = [
  {
    id: "ioe-bachelor",
    institution: "Institute of Engineering (IOE), Tribhuvan University",
    degree: "Bachelor of Engineering (B.E.)",
    field: "Electronics, Communication and Information Engineering",
    startDate: "2021-04",
    endDate: "2025-04",
    gpa: "84.10%",
    location: "Pulchowk Campus, Lalitpur, Nepal",
    description:
      "Comprehensive engineering program with focus on electronics, communication systems, information theory, and signal processing. Strong foundation in mathematics, algorithms, and systems design.",
    relevantCoursework: [
      "Data Structures & Algorithms",
      "Digital Signal Processing",
      "Communication Systems",
      "Computer Networks",
      "Machine Learning",
      "Operating Systems",
      "Microprocessor Systems",
      "Information Theory",
      "Probability & Statistics",
      "Network Programming",
    ],
    achievements: [
      "Maintained 84.10% average throughout the program",
      "Active member of IEEE Student Branch",
      "Research collaboration with Duke University (USA)",
      "Research collaboration with Universidad Politécnica de Valencia (Spain)",
      "Led LaTeX workshop for 100+ students",
    ],
  },
  {
    id: "st-xaviers-plus2",
    institution: "St. Xavier's College, Maitighar",
    degree: "+2 Science",
    field: "Physics, Chemistry, Mathematics",
    startDate: "2018-04",
    endDate: "2020-04",
    gpa: "3.79/4.00",
    location: "Kathmandu, Nepal",
    description:
      "Higher secondary education with strong foundation in science and mathematics, preparing for engineering studies.",
    achievements: [
      "Achieved GPA of 3.79/4.00",
      "Top 10% of graduating class",
      "Active member of mathematics and physics clubs",
    ],
  },
];

export const certifications: Certification[] = [
  {
    id: "isc2-cybersecurity",
    name: "Certified in Cybersecurity (CC)",
    issuer: "ISC2",
    date: "2025-02",
    description:
      "Professional certification covering cybersecurity fundamentals, risk management, security operations, and incident response.",
    credentialUrl: "https://www.isc2.org/Certifications/CC",
  },
];

export const contactInfo: ContactInfo = {
  email: "timalsinaditya@gmail.com",
  phone: "+977 9841368757",
  linkedin: "https://linkedin.com/in/aditya-timalsina",
  github: "https://github.com/timalsinaditya",
  location: "Kathmandu, Nepal",
  website: "https://aditya-timalsina.dev",
  cv: "/build/resume/aditya-resume-cso.pdf",
};

// Research Interests for PhD Applications
export const researchInterests: ResearchInterest[] = [
  {
    id: "ml-safety-critical",
    title: "Machine Learning for Safety-Critical Systems",
    description:
      "Developing robust, reliable, and interpretable machine learning models for real-time safety-critical applications. Focus on earthquake early warning, autonomous systems, and infrastructure monitoring where prediction accuracy and low false positive rates are crucial.",
    keywords: [
      "Deep Learning",
      "Real-time ML",
      "Safety-Critical Systems",
      "Robust AI",
      "Edge Computing",
      "Interpretable ML",
    ],
    relatedProjects: ["earthquake-warning", "audio-fft"],
  },
  {
    id: "next-gen-networks",
    title: "Next-Generation Network Systems",
    description:
      "Research in 5G/6G network protocols, IoT communication systems, and low-latency networking for vehicular, edge, and distributed computing applications. Interest in protocol design, optimization, and performance analysis.",
    keywords: [
      "5G/6G Networks",
      "V2X Communication",
      "IoT Protocols",
      "Network Optimization",
      "Edge Computing",
      "Low-Latency Systems",
    ],
    relatedProjects: ["v2x-communication"],
  },
  {
    id: "distributed-ml-systems",
    title: "Distributed Machine Learning Systems",
    description:
      "Building scalable distributed ML frameworks for training and inference across heterogeneous computing resources. Focus on system-level optimizations, communication efficiency, and fault tolerance in distributed learning.",
    keywords: [
      "Distributed Systems",
      "Systems for ML",
      "Federated Learning",
      "Edge ML",
      "Communication Efficiency",
      "Fault Tolerance",
    ],
    relatedProjects: ["distributed-neural-network", "blockchain-golang"],
  },
];

// Languages
export const languages = [
  { name: "English", level: "Fluent (Professional Working Proficiency)" },
  { name: "Nepali", level: "Native" },
  { name: "Hindi", level: "Intermediate" },
];

// Academic Skills & Competencies
export const academicSkills = [
  "Research Design & Methodology",
  "Technical Writing",
  "Data Analysis & Visualization",
  "Literature Review",
  "Experimental Design",
  "Performance Analysis",
  "Mathematical Modeling",
  "Scientific Programming",
  "Presentation & Communication",
  "Collaborative Research",
];

// Helper functions
export const getAllSkills = (): string[] => {
  return skills.reduce((allSkills, skillCategory) => {
    return [...allSkills, ...skillCategory.skills];
  }, [] as string[]);
};

export const getSkillsByProficiency = (
  proficiency: Skill["proficiency"]
): Skill[] => {
  return skills.filter((skill) => skill.proficiency === proficiency);
};

export const getSkillCount = (): number => {
  return skills.reduce((count, skillCategory) => {
    return count + skillCategory.skills.length;
  }, 0);
};

export const getEducationById = (id: string): Education | undefined => {
  return education.find((edu) => edu.id === id);
};

export const getResearchInterestById = (id: string): ResearchInterest | undefined => {
  return researchInterests.find((ri) => ri.id === id);
};